import React, { useState } from 'react';
import { IoClose, IoCalendarOutline, IoLinkOutline, IoCheckmarkCircle } from 'react-icons/io5';
import { DemonstratedInterestOpportunity, RecommendationResponse } from '@/services/demonstratedInterestService';
import './DemonstratedInterestModal.css';

interface DemonstratedInterestModalProps {
  isOpen: boolean;
  onClose: () => void;
  recommendation: RecommendationResponse;
  onAddOpportunities: (opportunityIds: number[]) => Promise<void>;
}

const DemonstratedInterestModal: React.FC<DemonstratedInterestModalProps> = ({
  isOpen,
  onClose,
  recommendation,
  onAddOpportunities,
}) => {
  const [selectedOpportunities, setSelectedOpportunities] = useState<number[]>(
    recommendation.opportunities.map((opp) => opp.id)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !recommendation.should_recommend) return null;

  const handleToggleOpportunity = (opportunityId: number) => {
    setSelectedOpportunities((prev) =>
      prev.includes(opportunityId)
        ? prev.filter((id) => id !== opportunityId)
        : [...prev, opportunityId]
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onAddOpportunities(selectedOpportunities);
      onClose();
    } catch (error) {
      console.error('Error adding opportunities:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getImportanceMessage = () => {
    if (recommendation.importance === 'strongly_considered') {
      return {
        title: 'Demonstrated Interest is Strongly Considered',
        message: `${recommendation.university_name} highly values demonstrated interest. We recommend registering for at least ${recommendation.recommended_count} events to significantly boost your application chances.`,
        color: 'text-red-600 dark:text-red-400',
      };
    } else if (recommendation.importance === 'somewhat_considered') {
      return {
        title: 'Demonstrated Interest is Considered',
        message: `${recommendation.university_name} considers demonstrated interest in their admissions process. Registering for ${recommendation.recommended_count} event can help strengthen your application.`,
        color: 'text-yellow-600 dark:text-yellow-400',
      };
    }
    return null;
  };

  const importanceInfo = getImportanceMessage();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="di-modal-overlay" onClick={onClose}>
      <div className="di-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="di-modal-header">
          <div>
            <h2 className="di-modal-title">Boost Your Application Chances</h2>
            {importanceInfo && (
              <p className={`di-modal-subtitle ${importanceInfo.color}`}>
                {importanceInfo.title}
              </p>
            )}
          </div>
          <button onClick={onClose} className="di-modal-close">
            <IoClose />
          </button>
        </div>

        <div className="di-modal-body">
          {importanceInfo && (
            <div className="di-importance-message">
              <p>{importanceInfo.message}</p>
            </div>
          )}

          <h3 className="di-opportunities-title">Recommended Opportunities</h3>

          <div className="di-opportunities-list">
            {recommendation.opportunities.map((opportunity) => {
              const isSelected = selectedOpportunities.includes(opportunity.id);
              return (
                <div
                  key={opportunity.id}
                  className={`di-opportunity-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleToggleOpportunity(opportunity.id)}
                >
                  <div className="di-opportunity-checkbox">
                    {isSelected && <IoCheckmarkCircle className="di-check-icon" />}
                  </div>
                  <div className="di-opportunity-content">
                    <h4 className="di-opportunity-title">{opportunity.title}</h4>
                    <p className="di-opportunity-description">{opportunity.description}</p>
                    <div className="di-opportunity-meta">
                      <span className="di-opportunity-date">
                        <IoCalendarOutline />
                        {formatDate(opportunity.event_date)}
                      </span>
                      {opportunity.signup_link && (
                        <span className="di-opportunity-link">
                          <IoLinkOutline />
                          Registration available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="di-modal-footer">
          <button onClick={onClose} className="di-button-secondary">
            Skip for Now
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedOpportunities.length === 0 || isSubmitting}
            className="di-button-primary"
          >
            {isSubmitting
              ? 'Adding...'
              : `Add ${selectedOpportunities.length} to Deadlines`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemonstratedInterestModal;
